import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { SearchContextProvider, useSearchContext } from "../../context/searchContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import { SearchConfiguration } from "../../http/types/configurations";
import { useConfigurationsApi } from "../../http/useConfigurations";
import { PaginationQuery } from "../../types";
import { formatTimeStamp } from "../../utils";
import SvgAddUsers from "../icons/AddUsers";
import SvgCopy from "../icons/Copy";
import SvgEdit from "../icons/Edit";
import SvgShare from "../icons/Share";
import SvgVisibility from "../icons/Visibility";
import FlexContent from "../ui/FlexContent";
import Header from "../ui/Header";
import Icon from "../ui/Icon";
import { Action, getDeleteAction } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import SearchBar from "../ui/SearchBar";
import Table from "../ui/Table";
import styles from "./Configurations.module.css";
import ConfigurationSharingAccessesDialog from "./ConfigurationSharingAccessesDialog";
import CreateConfigurationButton from "./CreateConfigurationButton";
import DuplicateConfigurationDialog from "./DuplicateConfigurationDialog";

const Configurations: FC = () => {
  return (
    <SearchContextProvider>
      <ConfigurationsContent />
    </SearchContextProvider>
  );
};

const ConfigurationsContent: FC = () => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  const navigate = useNavigate();
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const configurationsApi = useConfigurationsApi();
  const { actions, setActions } = useSearchContext();

  const debouncedValue = useDebounce(filters.state.q);
  const searchPlaceholder = i18n.translation.configurations.search;
  const createTitle = i18n.translation.common.createNew;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = configurationsApi.useConfigurations(query, aborter.current.signal);

  const [configurationToDelete, setConfigurationToDelete] = useState<SearchConfiguration>();
  const [configurationToDuplicate, setConfigurationToDuplicate] = useState<SearchConfiguration>();
  const [configurationToManageSharing, setConfigurationToManageSharing] = useState<SearchConfiguration>();
  const isCreateDisabled = !!error || !data;

  useEffect(() => {
    setActions(
      <>
        <SearchBar
          placeholder={searchPlaceholder}
          value={filters.state.q ?? ""}
          onChange={(term) => {
            aborter.current.abort();
            aborter.current = new AbortController();
            filters.setSearchTerm(term);
          }}
        />
        <CreateConfigurationButton type="configurations" title={createTitle} disabled={isCreateDisabled} />
      </>,
    );

    return () => {
      setActions(null);
    };
  }, [createTitle, filters.state.q, isCreateDisabled, searchPlaceholder, setActions]);

  const rowActions: (item: SearchConfiguration) => Action[] = (item) => [
    {
      title: item.canEdit ? i18n.translation.common.edit : i18n.translation.common.open,
      onClick: () => navigate(`/configurations/${item.id}`),
      icon: item.canEdit ? SvgEdit : SvgVisibility,
    },
    {
      title: i18n.translation.common.duplicate,
      onClick: () => setConfigurationToDuplicate(item),
      icon: SvgCopy,
    },
    {
      title: i18n.translation.projects.editSharings,
      onClick: () => item.author.id === auth.identity?.id && setConfigurationToManageSharing(item),
      icon: SvgAddUsers,
      isDisabled: item.author.id !== auth.identity?.id,
    },
    getDeleteAction(
      i18n,
      () => {
        item.author.id === auth.identity?.id && setConfigurationToDelete(item);
        return item.author.id !== auth.identity?.id;
      },
      true,
      item.author.id !== auth.identity?.id,
    ),
  ];

  return (
    <>
      <Header
        title={i18n.translation.configurations.plural}
        path={{ display: i18n.translation.configurations.my }}
        error={error}
        buttons={actions}
      />

      <FlexContent padding>
        <Table<SearchConfiguration>
          filters={filters}
          isTall
          head={
            <tr>
              <th align="left">{i18n.translation.common.name}</th>
              <th align="left">{i18n.translation.projects.singular}</th>
              <th align="left">{i18n.translation.systems.singular}</th>
              <th align="left">{i18n.translation.common.author}</th>
              <th align="left">{i18n.translation.common.lastModified}</th>
              <th></th>
            </tr>
          }
          items={data?.items}
          renderRow={(item) => (
            <>
              <td>
                <Link to={`/configurations/${item.id}`} onClick={(e) => e.stopPropagation()}>
                  {item.name}
                </Link>
              </td>
              <td>
                {item.project && (
                  <Link to={`/projects/${item.project.id}`} onClick={(e) => e.stopPropagation()}>
                    {item.project.name}
                  </Link>
                )}
              </td>
              <td>
                <Link to={`/systems/${item.system.id}`} onClick={(e) => e.stopPropagation()}>
                  {item.system.name}
                </Link>
              </td>
              <td title={item.author?.id !== auth.identity?.id ? i18n.translation.projects.shared : undefined}>
                {item.author?.firstName} {item.author?.lastName}
                {item.author?.id !== auth.identity?.id && <Icon glyph={SvgShare} className={styles.icon} />}
              </td>
              <td>{formatTimeStamp(item.modifiedAt, auth.identity?.language)}</td>
            </>
          )}
          rowActions={rowActions}
          onRowClick={(item) => navigate(`/configurations/${item.id}`)}
          total={data?.total}
          error={!!error}
        />
        {!!configurationToDelete && (
          <ModalDialog
            onClose={() => setConfigurationToDelete(undefined)}
            title={i18n.translation.configurations.delete}
            isSubmitting={isSubmitting}
            actions={[
              {
                title: i18n.translation.common.cancel,
                onClick: () => setConfigurationToDelete(undefined),
                type: "secondary",
              },
              {
                title: i18n.translation.common.yes,
                onClick: () => {
                  setIsSubmitting(true);
                  configurationsApi
                    .deleteConfiguration(configurationToDelete.project?.id, configurationToDelete.id)
                    .then(() => setConfigurationToDelete(undefined))
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                },
              },
            ]}
          >
            {i18n.translation.configurations.questions.delete}
          </ModalDialog>
        )}
        {!!configurationToDuplicate && (
          <DuplicateConfigurationDialog
            configuration={configurationToDuplicate}
            onClose={() => setConfigurationToDuplicate(undefined)}
            onSave={() => setConfigurationToDuplicate(undefined)}
          />
        )}
        {!!configurationToManageSharing && (
          <ConfigurationSharingAccessesDialog
            configurationId={configurationToManageSharing.id}
            configurationName={configurationToManageSharing.name}
            onClose={() => setConfigurationToManageSharing(undefined)}
          />
        )}
      </FlexContent>
    </>
  );
};

export default Configurations;
